import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";

import {RootState} from "../../../app/store";
import {RequestStatus} from "../../../api/apiConfiguration";
import {FranchiseListItem, FranchiseRequestPartial, updateFranchiseRequest} from "./franchiseSlice";

export type UpdateFranchiseRequestPartial = FranchiseRequestPartial;

export interface UpdateFranchiseState {
    updateFranchiseRequest?: UpdateFranchiseRequestPartial;
    updateFranchiseRequestStatus: RequestStatus;
}

const buildInitialState = (): UpdateFranchiseState => ({
    updateFranchiseRequestStatus: RequestStatus.NONE
});

const initialState: UpdateFranchiseState = buildInitialState();

export const updateFranchise = createAsyncThunk<void, undefined, { state: RootState }>(
    "franchiseUpdate/update",
    (_payload, {getState}) => {
        const request = getState().franchiseUpdate.updateFranchiseRequest;
        if (!request) {
            throw new Error("update franchise request not initialised");
        }
        return updateFranchiseRequest(request);
    },
    {
        condition: (_payload, {getState}) =>
            getState().franchiseUpdate.updateFranchiseRequestStatus !== RequestStatus.PENDING
    }
);

export const franchiseUpdateSlice = createSlice({
    name: "franchiseUpdate",
    initialState,
    reducers: {
        clearUpdateFranchiseState: () => buildInitialState(),
        prepareUpdateFranchiseRequest: (state, {payload}: PayloadAction<FranchiseListItem>) => {
            state.updateFranchiseRequest = {...payload};
        },
        updateUpdateFranchiseRequest: (state, {payload}: PayloadAction<UpdateFranchiseRequestPartial>) => {
            state.updateFranchiseRequest = {
                ...state.updateFranchiseRequest,
                ...payload
            };
        }
    },
    extraReducers: builder => {
        builder.addCase(updateFranchise.pending, state => {
            state.updateFranchiseRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(updateFranchise.fulfilled, state => {
            state.updateFranchiseRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(updateFranchise.rejected, state => {
            state.updateFranchiseRequestStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    clearUpdateFranchiseState,
    prepareUpdateFranchiseRequest,
    updateUpdateFranchiseRequest
} = franchiseUpdateSlice.actions;

export const updateFranchiseRequestSelector = (state: RootState): UpdateFranchiseRequestPartial =>
    state.franchiseUpdate.updateFranchiseRequest || {};
export const updateFranchiseRequestStatusSelector = (state: RootState): RequestStatus =>
    state.franchiseUpdate.updateFranchiseRequestStatus;
